import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { clearSensitiveClientData, saveRememberedAccount } from '../utils/rememberedAccount';

const AuthContext = createContext(null);
const TOKEN_KEY = 'authToken';

const applyAuthHeader = (token) => {
  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common.Authorization;
  }
};

/** Jeder Request: Token aus localStorage — verhindert Race ohne Authorization-Header */
if (!axios.__odiumAuthRequestInterceptor) {
  axios.__odiumAuthRequestInterceptor = axios.interceptors.request.use((config) => {
    const headers = config.headers || {};
    const hasAuth =
      Boolean(headers.Authorization || headers.authorization) ||
      Boolean(axios.defaults.headers.common.Authorization);
    if (!hasAuth) {
      const saved = localStorage.getItem(TOKEN_KEY) || '';
      if (saved) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${saved}`;
      }
    }
    return config;
  });
}

/** JWT-Payload lesen (ohne Verify — nur für Anzeige/Abgleich). */
function decodeJwtPayload(token) {
  try {
    const part = String(token || '').split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
    return JSON.parse(json);
  } catch (_) {
    return null;
  }
}

function normalizeUser(raw, tokenFallback = '') {
  if (!raw || typeof raw !== 'object') return null;
  const fromJwt = decodeJwtPayload(tokenFallback);
  const id = Number(raw.id ?? fromJwt?.id) || null;
  const email = String(raw.email || fromJwt?.email || '')
    .trim()
    .toLowerCase();
  if (!id && !email) return null;
  return {
    ...raw,
    id: id || raw.id,
    email: email || String(raw.email || '').trim(),
  };
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => {
    const saved = localStorage.getItem(TOKEN_KEY) || '';
    applyAuthHeader(saved || null);
    return saved;
  });
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef(token);
  const userRef = useRef(user);
  const syncingRef = useRef(false);
  /** Steigt bei jedem Session-Wechsel — veraltete /api/auth/me Responses werden verworfen */
  const sessionEpochRef = useRef(0);
  tokenRef.current = token;
  userRef.current = user;

  const bumpSessionEpoch = useCallback(() => {
    sessionEpochRef.current += 1;
    return sessionEpochRef.current;
  }, []);

  const logout = useCallback(() => {
    bumpSessionEpoch();
    localStorage.removeItem(TOKEN_KEY);
    clearSensitiveClientData();
    setToken('');
    setUser(null);
    applyAuthHeader(null);
  }, [bumpSessionEpoch]);

  const loadUserForToken = useCallback(async (savedToken, { epoch = null } = {}) => {
    const requestEpoch = epoch == null ? sessionEpochRef.current : epoch;
    if (!savedToken) {
      if (requestEpoch !== sessionEpochRef.current) return null;
      applyAuthHeader(null);
      setToken('');
      setUser(null);
      return null;
    }
    applyAuthHeader(savedToken);
    const res = await axios.get('/api/auth/me', {
      headers: { Authorization: `Bearer ${savedToken}` },
    });
    // Session hat sich während des Requests geändert → Ergebnis verwerfen
    if (requestEpoch !== sessionEpochRef.current) return null;
    if (localStorage.getItem(TOKEN_KEY) !== savedToken) return null;

    if (!res.data?.user) {
      throw new Error('Session ungültig');
    }
    const nextUser = normalizeUser(res.data.user, savedToken);
    if (!nextUser?.email) {
      throw new Error('Session ohne E-Mail');
    }
    setToken(savedToken);
    setUser(nextUser);
    return nextUser;
  }, []);

  /** Session aus localStorage übernehmen (Login/Logout in anderem Tab) + bei Fokus neu laden */
  const syncSessionFromStorage = useCallback(async ({ forceRefresh = false } = {}) => {
    if (syncingRef.current) return;
    const saved = localStorage.getItem(TOKEN_KEY) || '';

    if (saved === tokenRef.current) {
      if (!saved) {
        if (userRef.current) setUser(null);
        return;
      }
      // Gleicher Token: trotzdem User vom Server holen (verhindert „alte E-Mail“ in der UI)
      if (!forceRefresh && userRef.current?.email) return;
      syncingRef.current = true;
      const epoch = sessionEpochRef.current;
      try {
        await loadUserForToken(saved, { epoch });
      } catch (_) {
        if (epoch === sessionEpochRef.current) logout();
      } finally {
        syncingRef.current = false;
      }
      return;
    }

    syncingRef.current = true;
    const epoch = bumpSessionEpoch();
    try {
      if (!saved) {
        clearSensitiveClientData();
        setToken('');
        setUser(null);
        applyAuthHeader(null);
        return;
      }
      clearSensitiveClientData();
      // Sofort E-Mail aus JWT anzeigen, bevor /me fertig ist
      const preview = normalizeUser(decodeJwtPayload(saved), saved);
      if (preview?.email) setUser(preview);
      setToken(saved);
      applyAuthHeader(saved);
      await loadUserForToken(saved, { epoch });
    } catch (_) {
      if (epoch === sessionEpochRef.current) logout();
    } finally {
      syncingRef.current = false;
    }
  }, [bumpSessionEpoch, loadUserForToken, logout]);

  const applySession = useCallback((session) => {
    if (!session?.token) {
      throw new Error(session?.error || 'Login fehlgeschlagen');
    }
    const epoch = bumpSessionEpoch();
    clearSensitiveClientData();
    localStorage.setItem(TOKEN_KEY, session.token);
    const nextUser = normalizeUser(session.user, session.token);
    setToken(session.token);
    applyAuthHeader(session.token);
    setUser(nextUser);
    setLoading(false);
    if (nextUser?.email) {
      saveRememberedAccount(nextUser.email, session.password || '', session.mail || {}, {
        authType: session.authType || (session.password ? 'password' : 'google'),
      });
    }
    // User zusätzlich vom Server bestätigen (verwirft bei neuem Login automatisch)
    loadUserForToken(session.token, { epoch }).catch(() => {});
    return { user: nextUser, mail: session.mail };
  }, [bumpSessionEpoch, loadUserForToken]);

  const login = useCallback(async (email, password, mailOptions = {}) => {
    const payload = {
      email,
      password,
      imapHost: mailOptions.imapHost || mailOptions.host,
      smtpHost: mailOptions.smtpHost || mailOptions.imapHost || mailOptions.host,
      imapPort: mailOptions.imapPort,
      smtpPort: mailOptions.smtpPort,
    };
    const res = await axios.post('/api/auth/login', payload, { timeout: 35000 });
    return applySession({
      token: res.data?.token,
      user: res.data?.user || null,
      mail: res.data?.mail || {
        imapHost: payload.imapHost,
        smtpHost: payload.smtpHost,
        imapPort: payload.imapPort,
        smtpPort: payload.smtpPort,
      },
      password,
      authType: 'password',
      error: res.data?.error,
    });
  }, [applySession]);

  const completeGoogleLogin = useCallback(async (exchangeCode) => {
    const res = await axios.post('/api/auth/google/exchange', { code: exchangeCode });
    return applySession({
      token: res.data?.token,
      user: res.data?.user || null,
      mail: res.data?.mail || null,
      password: '',
      authType: res.data?.authType || 'google',
      error: res.data?.error,
    });
  }, [applySession]);

  useEffect(() => {
    let cancelled = false;

    const isBackendDown = (error) => {
      if (!error) return false;
      if (error.response?.status === 503) return true;
      if (error.response?.data?.code === 'ECONNREFUSED' || error.response?.data?.code === 'STARTING') {
        return true;
      }
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') return true;
      if (error.message === 'Network Error') return true;
      return false;
    };

    const bootstrap = async () => {
      const epoch = bumpSessionEpoch();
      clearSensitiveClientData();
      const saved = localStorage.getItem(TOKEN_KEY) || '';
      if (!saved) {
        applyAuthHeader(null);
        if (!cancelled && epoch === sessionEpochRef.current) {
          setUser(null);
          setLoading(false);
        }
        return;
      }

      // Sofort Anzeige aus JWT, bis /me kommt
      const preview = normalizeUser(decodeJwtPayload(saved), saved);
      if (preview?.email && !cancelled) setUser(preview);

      const maxAttempts = 15;
      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        if (cancelled || epoch !== sessionEpochRef.current) {
          // Neuer Login hat Session übernommen — Loading nicht hängen lassen
          if (!cancelled && sessionEpochRef.current !== epoch) {
            setLoading(false);
          }
          return;
        }
        try {
          await loadUserForToken(saved, { epoch });
          break;
        } catch (err) {
          if (isBackendDown(err) && attempt < maxAttempts) {
            await new Promise((r) => setTimeout(r, 600));
            continue;
          }
          if (!cancelled && epoch === sessionEpochRef.current) logout();
          break;
        }
      }
      if (!cancelled && epoch === sessionEpochRef.current) setLoading(false);
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [logout, loadUserForToken, bumpSessionEpoch]);

  // Anderer Tab: Login / Logout / Account-Wechsel (+ Fokus: User neu vom Server laden)
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== TOKEN_KEY && event.key !== null) return;
      syncSessionFromStorage({ forceRefresh: true });
    };
    const onFocus = () => {
      syncSessionFromStorage({ forceRefresh: true });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        syncSessionFromStorage({ forceRefresh: true });
      }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [syncSessionFromStorage]);

  useEffect(() => {
    const onUnauthorized = (error) => {
      if (error?.response?.status === 401) {
        const url = String(error?.config?.url || '');
        // Polling-/Badge-Endpoints: kein harter Logout (Race beim Start / Fallback)
        const softUnauthorized =
          url.includes('/api/emails/folder-counts') ||
          url.includes('/api/emails/unread-count') ||
          url.includes('/api/composer-drafts');
        if (
          !softUnauthorized &&
          !url.includes('/api/auth/login') &&
          !url.includes('/api/auth/google/exchange') &&
          !url.includes('/api/auth/google/code')
        ) {
          logout();
        }
      }
      return Promise.reject(error);
    };
    const id = axios.interceptors.response.use((res) => res, onUnauthorized);
    return () => axios.interceptors.response.eject(id);
  }, [logout]);

  const value = useMemo(() => ({
    token,
    user,
    loading,
    isAuthenticated: !!token && !!user?.email,
    login,
    completeGoogleLogin,
    logout,
  }), [token, user, loading, login, completeGoogleLogin, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
